import React from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Award } from "lucide-react";

const DEFAULT_CFG = {
    ron_per_point: 10,
    redemption_rate: 0.10,
    min_redeem: 100,
    tiers: [
        { name: "Bronze", min_points: 0,    discount_percent: 0, color: "#b45309" },
        { name: "Silver", min_points: 500,  discount_percent: 3, color: "#64748b" },
        { name: "Gold",   min_points: 2000, discount_percent: 5, color: "#f59e0b" },
    ],
};

export default function AdminLoyalty() {
    const [cfg, setCfg] = React.useState(null);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        api.get("/admin/loyalty/config").then(r => {
            const data = r.data;
            // Sort tiers ascending by min_points for display
            const tiers = [...(data.tiers || DEFAULT_CFG.tiers)].sort((a, b) => a.min_points - b.min_points);
            setCfg({ ...data, tiers });
        }).catch(() => setCfg({ ...DEFAULT_CFG }));
    }, []);

    const setTier = (i, field, val) => {
        setCfg(c => {
            const tiers = c.tiers.map((t, idx) => idx === i ? { ...t, [field]: val } : t);
            return { ...c, tiers };
        });
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/loyalty/config", cfg);
            toast.success("Configurație salvată");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Eroare la salvare");
        }
        setSaving(false);
    };

    if (!cfg) return <div className="text-muted-foreground">Se încarcă...</div>;

    const redemptionExample = cfg.min_redeem * cfg.redemption_rate;

    return (
        <div className="max-w-2xl">
            <div className="overline mb-1">Administrare</div>
            <h1 className="font-heading text-3xl mb-2 flex items-center gap-3">
                <Award className="h-7 w-7 text-primary" /> Puncte de fidelizare
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
                Configurează cum se acordă și cum se răscumpără punctele, și pragurile pentru fiecare nivel.
            </p>

            {/* Acumulare */}
            <div className="card-soft p-5 mb-4 space-y-4">
                <h3 className="font-heading text-lg">Acumulare puncte</h3>
                <div className="flex items-end gap-3">
                    <div className="flex-1">
                        <label className="text-xs text-muted-foreground">RON cheltuiți pentru 1 punct</label>
                        <input
                            type="number" min="1" step="1"
                            value={cfg.ron_per_point}
                            onChange={e => setCfg(c => ({ ...c, ron_per_point: Math.max(1, parseInt(e.target.value) || 1) }))}
                            className="input-soft"
                        />
                    </div>
                    <div className="pb-2.5 text-sm text-muted-foreground whitespace-nowrap">
                        = 1 punct
                    </div>
                </div>
                <p className="text-xs text-muted-foreground">
                    Ex: valoarea <strong>{cfg.ron_per_point}</strong> înseamnă că la o comandă de 300 RON clientul primește <strong>{Math.round(300 / cfg.ron_per_point)}</strong> puncte.
                </p>
            </div>

            {/* Răscumpărare */}
            <div className="card-soft p-5 mb-4 space-y-4">
                <h3 className="font-heading text-lg">Răscumpărare puncte</h3>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="text-xs text-muted-foreground">Valoare 1 punct (RON)</label>
                        <input
                            type="number" min="0.01" step="0.01"
                            value={cfg.redemption_rate}
                            onChange={e => setCfg(c => ({ ...c, redemption_rate: parseFloat(e.target.value) || 0.01 }))}
                            className="input-soft"
                        />
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">Minim puncte pentru răscumpărare</label>
                        <input
                            type="number" min="1" step="1"
                            value={cfg.min_redeem}
                            onChange={e => setCfg(c => ({ ...c, min_redeem: Math.max(1, parseInt(e.target.value) || 1) }))}
                            className="input-soft"
                        />
                    </div>
                </div>
                <p className="text-xs text-muted-foreground">
                    Ex: {cfg.min_redeem} puncte (minimul) valorează <strong>{redemptionExample.toFixed(2)} RON</strong> reducere la comandă.
                </p>
            </div>

            {/* Niveluri */}
            <div className="card-soft p-5 mb-6 space-y-3">
                <h3 className="font-heading text-lg">Niveluri (tiers)</h3>
                <p className="text-xs text-muted-foreground">
                    Nivelul de bază (Bronze) pornește întotdeauna de la 0 puncte. Celelalte niveluri se activează când clientul acumulează cel puțin numărul de puncte setat.
                </p>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border">
                                <th className="text-left py-2 pr-3 text-xs text-muted-foreground font-medium">Nivel</th>
                                <th className="text-left py-2 pr-3 text-xs text-muted-foreground font-medium">Minim puncte</th>
                                <th className="text-left py-2 pr-3 text-xs text-muted-foreground font-medium">Discount %</th>
                                <th className="text-left py-2 text-xs text-muted-foreground font-medium">Culoare</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border/50">
                            {cfg.tiers.map((tier, i) => (
                                <tr key={i}>
                                    <td className="py-2.5 pr-3">
                                        <div className="flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: tier.color }} />
                                            <input
                                                value={tier.name}
                                                onChange={e => setTier(i, "name", e.target.value)}
                                                className="input-soft py-1 text-sm font-medium w-24"
                                            />
                                        </div>
                                    </td>
                                    <td className="py-2.5 pr-3">
                                        <input
                                            type="number" min="0" step="1"
                                            value={tier.min_points}
                                            disabled={i === 0}
                                            onChange={e => setTier(i, "min_points", Math.max(0, parseInt(e.target.value) || 0))}
                                            className={`input-soft py-1 text-sm w-24 ${i === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                                        />
                                    </td>
                                    <td className="py-2.5 pr-3">
                                        <div className="flex items-center gap-1">
                                            <input
                                                type="number" min="0" max="100" step="0.5"
                                                value={tier.discount_percent}
                                                onChange={e => setTier(i, "discount_percent", Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                                                className="input-soft py-1 text-sm w-20"
                                            />
                                            <span className="text-muted-foreground">%</span>
                                        </div>
                                    </td>
                                    <td className="py-2.5">
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="color"
                                                value={tier.color}
                                                onChange={e => setTier(i, "color", e.target.value)}
                                                className="w-8 h-8 rounded cursor-pointer border border-border bg-transparent p-0.5"
                                            />
                                            <input
                                                value={tier.color}
                                                onChange={e => setTier(i, "color", e.target.value)}
                                                className="input-soft py-1 text-xs font-mono w-24"
                                                placeholder="#rrggbb"
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <button onClick={save} disabled={saving} className="btn-primary">
                {saving ? "Se salvează..." : "Salvează configurația"}
            </button>
        </div>
    );
}
